import type { FlagProperties, FlagState } from '$models/FeatureFlags/FeatureFlags';
import { browser } from '$app/environment';
import { goto } from '$app/navigation';
import { getCookie, updateCookie } from '$lib/cookies';
import { trackExperimentAssignment } from '$lib/datalayer/datalayer';
import { parseCookieValue } from '$lib/featureFlags/cookies';
import { flags as flagsState } from '$lib/state/featureFlags.svelte';

/**
 * Parses URL parameters for feature flags
 */
export function parseFeatureFlagParams(
	urlParamPrefix: string,
	validFlags: FlagState
): Partial<FlagState> {
	if (!browser) return {};

	const params = new URLSearchParams(window.location.search);
	const flagParams: Partial<FlagState> = {};

	// Iterate through all URL parameters
	for (const [key, value] of params) {
		// Check if parameter is a feature flag parameter
		if (key.startsWith(urlParamPrefix)) {
			// Extract flag name by removing prefix
			const flagName = key.substring(urlParamPrefix.length);

			// Check if this is a valid feature flag
			if (flagName in validFlags) {
				// Parse boolean value
				const boolValue = value.toLowerCase() === 'true';
				flagParams[flagName] = boolValue;
			}
		}
	}

	return flagParams;
}

/**
 * Updates feature flag cookie from URL parameters and removes params from URL afterwords
 */
export function updateFeatureFlagsFromUrl(
	urlParamPrefix: string,
	validFlags: FlagState,
	cookieName: string
): FlagState {
	if (!browser) return {};

	// Get flags from URL parameters
	const urlFlags = parseFeatureFlagParams(urlParamPrefix, validFlags);

	// Get current cookie flags
	const cookieValue = getCookie(cookieName);
	const currentCookieFlags = cookieValue ? parseCookieValue(cookieValue) : {};

	// If URL flags were found, update cookie and remove them from URL
	if (Object.keys(urlFlags).length > 0) {
		// Merge URL flags with current flags (URL takes precedence)
		const updatedFlags = { ...currentCookieFlags, ...urlFlags } as FlagState;

		// Update cookie with merged flags (session cookie - removed when browser closes)
		updateCookie(cookieName, updatedFlags, {
			path: '/',
			sameSite: 'lax'
			// No expires or maxAge - makes this a session cookie
		});

		// @TODO: Think of a nicer/cleaner way to do this (with events?) when flags can updated other than from URL in the future
		trackExperimentAssignment(updatedFlags);

		// Remove feature flag params from URL
		removeFeatureFlagParamsFromUrl(urlParamPrefix, validFlags);

		return updatedFlags;
	}

	// If no URL flags, return current cookie state
	return currentCookieFlags as FlagState;
}

/**
 * Removes feature flag parameters from the URL and updates browser history
 */
function removeFeatureFlagParamsFromUrl(urlParamPrefix: string, validFlags: FlagState): void {
	if (!browser) return;

	const url = new URL(window.location.href);
	const params = new URLSearchParams(url.search);
	let paramsRemoved = false;

	// Remove all feature flag parameters
	for (const [key] of Array.from(params.entries())) {
		if (key.startsWith(urlParamPrefix)) {
			const flagName = key.substring(urlParamPrefix.length);
			if (flagName in validFlags) {
				params.delete(key);
				paramsRemoved = true;
			}
		}
	}

	// @TODO: This is turned off because VWO kept reloading the page when the URL params were removed
	// // Update URL if any params were removed
	// if (paramsRemoved) {
	// 	url.search = params.toString();

	// 	// Use replaceState in stead of goto to update URL without adding to history or triggering navigation
	// 	window.history.replaceState({}, '', url.pathname + url.search + url.hash);
	// }
}

/**
 * Updates feature flags from URL parameters and syncs with cookies
 * Should be called on client mount with the config and settings provided by the server (on build time)
 */
export function maybeUpdateFeatureFlagsFromUrl(flags: FlagProperties): void {
	// Get updated flags from URL (this also updates the cookie)
	const updatedFlags = updateFeatureFlagsFromUrl(
		flags.config.urlParamPrefix,
		flags.flags,
		flags.config.cookieName
	);

	// Update the reactive state with the merged flags
	if (Object.keys(updatedFlags).length > 0) {
		flagsState.updateFlags(updatedFlags);
	}
}

export function updateFeatureFlags(flags: FlagState) {
	flagsState.updateFlags(flags);
}
